import React from "react"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import PeopleIcon from '@mui/icons-material/People';
import LabelIcon from "@mui/icons-material/Label";
import FlagIcon from "@mui/icons-material/Flag";
import EuroIcon from '@mui/icons-material/Euro';
import NotesIcon from '@mui/icons-material/Notes';
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormEditPeriod from "./FormEditPeriod";
import FormEditGuests from "./FormEditGuests";
import FormEditGuest from "./FormEditGuest";
import useDialog from "../../../../hooks/useDialog";
import AttributeList, {AttributeListFormComponents} from "../../../../components/AttributeList";
import {useFeedbackTools} from "../../../../components/FeedbackTools";
import {DateFormatter} from "../../../../services/date";
import CountryAttributeForm from "../../../../components/AttributeForm/CountryAttributeForm";
import CountryFlag from "../../../../components/CountryFlag";
import FormEditDeposit from "./FormEditDeposit";
import TextAttributeForm from "../../../../components/AttributeForm/TextAttributeForm";

import {useBookingUpdate, useBookingUpdatePeriod} from "../../query";
import {ResponsiveDialog} from "../../../../components/ResponsiveDialog";


const EditDialog = ({booking, onClose}) => {
    const {showErrorNotification, showSuccessNotification} = useFeedbackTools();

    const bookingUpdate = useBookingUpdate();
    const bookingUpdatePeriod = useBookingUpdatePeriod();

    const formPeriod = useDialog();
    const formGuests = useDialog();
    const formGuest = useDialog();
    const formNationality = useDialog();
    const formDeposit = useDialog();
    const formNotes = useDialog();

    // If any of attribute form is visible the edit dialog shouldn't be visible
    const visible = !(formPeriod.show || formGuests.show || formGuest.show || formNationality.show || formDeposit.show || formNotes.show);

    const handleSubmit = async (formDialog, data) => {
        formDialog.setIsLoading(true)
        try {
            await bookingUpdate.mutateAsync({id: booking.id, data});
            formDialog.close();
            onClose();  // close the entire edit dialog (unmount)
            showSuccessNotification("Aggiornamento effettuato")
        } catch (e) {
            showErrorNotification(e)
        }
        formDialog.setIsLoading(false)
    }

    const handlePeriodSubmit = async (formDialog, data) => {
        formDialog.setIsLoading(true)
        try {
            await bookingUpdatePeriod.mutateAsync({id: booking.id, data});
            formDialog.close();
            onClose();  // close the entire edit dialog (unmount)
            showSuccessNotification("Periodo aggiornato")
        } catch (e) {
            showErrorNotification(e)
        }
        formDialog.setIsLoading(false)
    }

    const attributeFormCloseHandler = (form) => {
        return () => {
            form.close();
            onClose();  // close the entire edit dialog (unmount)
        }
    }

    const bookingAttributes = [
        {
            label: "Periodo",
            value: getPeriodDescription(booking),
            defaultValue: "Non indicato",
            icon: <CalendarTodayIcon/>,
            formDialog: formPeriod,
            readOnly: !booking.isPeriodEditable(),
            formComponent: <FormEditPeriod booking={booking}
                                           onClose={attributeFormCloseHandler(formPeriod)}
                                           onSubmit={(data) => handlePeriodSubmit(formPeriod, data)}
                                           isLoading={formPeriod.loading}/>
        },
        {
            label: "Ospiti",
            value: booking.getGuestsSummary(),
            defaultValue: "Non indicato",
            icon: <PeopleIcon/>,
            formDialog: formGuests,
            readOnly: false,
            formComponent: <FormEditGuests booking={booking}
                                           onClose={attributeFormCloseHandler(formGuests)}
                                           onSubmit={(data) => handleSubmit(formGuests, data)}
                                           isLoading={formGuests.loading}/>
        },
        {
            label: "Referente e Email",
            value: getGuestDescription(booking),
            defaultValue: "Non indicati",
            icon: <LabelIcon/>,
            formDialog: formGuest,
            readOnly: false,
            formComponent: <FormEditGuest booking={booking}
                                          onClose={attributeFormCloseHandler(formGuest)}
                                          onSubmit={(data) => handleSubmit(formGuest, data)}
                                          isLoading={formGuest.loading}/>
        },
        {
            label: "Nazionalità",
            value: getNationalityDescription(booking),
            defaultValue: "Non indicata",
            icon: <FlagIcon/>,
            formDialog: formNationality,
            readOnly: false,
            formComponent: <CountryAttributeForm value={booking.nationality?.code}
                                                 attribute="nationality"
                                                 label="Nazionalità"
                                                 onClose={attributeFormCloseHandler(formNationality)}
                                                 onSubmit={(data) => handleSubmit(formNationality, data)}
                                                 isLoading={formNationality.loading}/>
        }
    ]

    const bookingConditionsAttributes = [
        {
            label: "Caparra",
            value: `${booking.bookingFinance.deposit ? booking.bookingFinance.deposit.toFixed(2) : '-.--'} €`,
            defaultValue: "Non indicata",
            icon: <EuroIcon/>,
            formDialog: formDeposit,
            readOnly: false,
            formComponent: <FormEditDeposit booking={booking}
                                            onClose={attributeFormCloseHandler(formDeposit)}
                                            onSubmit={(data) => handleSubmit(formDeposit, data)}
                                            isLoading={formDeposit.loading}/>
        }
    ]

    const notesAttributes = [
        {
            label: "Note Host",
            value: booking.hostNotes,
            defaultValue: "Nessuna nota",
            icon: <NotesIcon/>,
            formDialog: formNotes,
            readOnly: false,
            formComponent: <TextAttributeForm value={booking.hostNotes}
                                              attribute="hostNotes"
                                              label="Note Host"
                                              multiline
                                              rows={6}
                                              onClose={attributeFormCloseHandler(formNotes)}
                                              onSubmit={(data) => handleSubmit(formNotes, data)}
                                              isLoading={formNotes.loading}
            />
        }
    ]

    return <>
        <ResponsiveDialog title="Modifica prenotazione"
                          isOpen={visible}
                          maxWidth="md"
                          scroll="paper"
                          closeLabel="Chiudi"
                          onClose={onClose}>
            <Stack>
                <Typography variant='subtitle2'>
                    Periodo e Ospiti
                </Typography>
                <AttributeList attributes={bookingAttributes} appendFormComponents={false}/>
                <Typography variant='subtitle2'>
                    Caparra
                </Typography>
                <AttributeList attributes={bookingConditionsAttributes} appendFormComponents={false}/>
                <Typography variant='subtitle2'>
                    Note
                </Typography>
                <AttributeList attributes={notesAttributes} appendFormComponents={false}/>
            </Stack>
        </ResponsiveDialog>

        {/*The AttributeListFormComponents should be placed outside the Responsive dialog
          * This way when we close the responsive dialog to open an attribute form dialog it doesn't
          * disappear
          */}
        <AttributeListFormComponents attributes={bookingAttributes} />
        <AttributeListFormComponents attributes={bookingConditionsAttributes} />
        <AttributeListFormComponents attributes={notesAttributes} />
    </>
}


const getNationalityDescription = (booking) => {
    if(booking.nationality){
        return <>
            <CountryFlag code={booking.nationality.code}/> {booking.nationality.code}
        </>
    }
    return null;
}

const getGuestDescription = (booking) => {
    const fullName = booking.getFullName();
    const email = booking.email;
    return [fullName, email].filter(x => x !== undefined && x !== null && x !== "").join(', ');
}

const getPeriodDescription = (booking) => {
    if (booking.getArrival() == null || booking.getDeparture() == null) {
        return null
    }
    return `${DateFormatter.shortStringWithDayName(booking.getArrival())} - ${DateFormatter.shortStringWithDayName(booking.getDeparture())}`
}

export default EditDialog;